import { refreshAccessToken } from './token-refresh';

/**
 * Decode JWT token to get expiration time
 */
const decodeToken = (token: string): { exp?: number; iat?: number } | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

/**
 * Check if a token is expired or will expire soon (within 60 seconds)
 */
const isTokenExpired = (token: string): boolean => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true; // If we can't decode, assume expired
  }
  // Check if token expires within the next 60 seconds
  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = 60 * 1000; // 60 seconds buffer
  return currentTime >= expirationTime - bufferTime;
};

/**
 * Get a fresh, valid token from Auth0
 * This function ensures we always get a non-expired token by:
 * 1. Getting the token from getIdTokenClaims
 * 2. Checking if it's expired
 * 3. If expired, refreshing it using the refresh token mechanism
 */
export const getFreshToken = async (
  getIdTokenClaims: () => Promise<any>,
): Promise<string | null> => {
  try {
    // Get the current token from Auth0
    const claims = await getIdTokenClaims();
    const currentToken = claims?.__raw;

    if (!currentToken) {
      // If no token, try to refresh
      return await refreshAccessToken();
    }

    // Check if the token is expired or about to expire
    if (isTokenExpired(currentToken)) {
      // Token is expired, try to refresh it
      const refreshedToken = await refreshAccessToken();
      if (refreshedToken) {
        // Update localStorage and Auth0 cache
        localStorage.setItem('idToken', refreshedToken);
        return refreshedToken;
      }
      // If refresh failed, return the current token anyway
      // The axios interceptor will handle 401 errors
      return currentToken;
    }

    // Token is still valid, return it
    // Also update localStorage to keep it in sync
    const storedToken = localStorage.getItem('idToken');
    if (storedToken !== currentToken) {
      localStorage.setItem('idToken', currentToken);
    }
    return currentToken;
  } catch (error) {
    // If getIdTokenClaims fails, try to refresh
    const refreshedToken = await refreshAccessToken();
    return refreshedToken;
  }
};

